<template>
  <div
    id="review-section"
    ref="reviewSectionContainer"
    class="review-section-container"
    :class="{'condensed': isCondensable && isCondensed}"
  >
    <div v-if="showSkeleton">
      <h3 class="small-margin-bottom-s large-margin-bottom-m">
        Ratings & Reviews
      </h3>
      <Skeleton
        height="588px"
      />
    </div>
    <div
      v-else
      class="review-section"
    >
      <NoReviews v-if="noReviews" />
      <div v-else>
        <RatingAndReviews v-if="isStatisticsReady" />
        <LazyLoadWhenInViewport>
          <FrequentlyMentionedChips
            v-if="frequentlyMentioned?.length > 0"
            class="margin-bottom-s"
          />
        </LazyLoadWhenInViewport>

        <SearchAndFilterBar
          :id="REVIEWS_SEARCH_ID"
          class="margin-bottom-m search-filter"
        />
        <div class="margin-bottom-s grid-x">
          <h5 class="margin-right-xs">
            {{ reviewsHeader }}
          </h5>
          <button
            v-if="isFiltered"
            class="link-body"
            @click="resetFilters"
          >
            Clear
          </button>
        </div>
        <div
          ref="firstReviewRef"
          class="margin-bottom-s"
        >
          <Review
            v-for="review in reviews"
            :key="review.reviewId"
            :review="review"
            :is-comment-form-shown="openCommentId === review.reviewId"
            @toggleComment="handleToggleComment"
            @onSelectPhoto="displayFullReview"
          />
          <FullReviewModal
            :id="INDIVIDUAL_REVIEW_MODAL"
            :photos="photos"
            :reviews="selectedReview"
            :selected-index="clickedPhotoIndex"
            :is-loading="false"
          />
        </div>
        <ReviewsPagination />
      </div>
      <button
        v-if="isCondensable"
        ref="reviewsToggle"
        class="button secondary reviews-toggle large-margin-top-xxs small-margin-top-m"
        @click="toggleIsCondensed"
      >
        {{ toggleBtnText }}
      </button>
    </div>
  </div>
</template>

<script setup>
import {
  computed, watch, onMounted, ref, defineAsyncComponent,
} from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import Skeleton from '@common-vue/atomic-ui/src/components/skeleton';
import { useDeviceType } from '@composables/utils/useDeviceType';
import { useReviews } from '@composables/reviews/useReviews';
import Review from '@components/reviews/IndividualGenericReview';
import { useReviewsStatistics } from '@composables/reviews/useReviewsStatistics';
import LazyLoadWhenInViewport from '@components/common/LazyLoadWhenInViewport';
import NoReviews from '@components/reviews/NoReviews';
import FullReviewModal from '@components/reviews/FullReviewModal';
import SearchAndFilterBar from '@components/reviews/SearchAndFilterBar';
import ReviewsPagination from '@components/reviews/ReviewsPagination';
import { useModal } from '@composables/utils/useModal';
import { useProduct } from '@modules/product';
import { useProductFlags } from '@modules/featureControl';
import useSelectedMemberId from '@modules/latchkey/composables/useSelectedMemberId';
import { trackPageView, trackImpression } from '@utils/bvPixel';
import RatingAndReviews from './reviews/RatingAndReviews/RatingAndReviews';
import { REVIEWS_SEARCH_ID } from '../constants/reviews';

const FrequentlyMentionedChips = defineAsyncComponent(() => import(/* webpackChunkName: 'FrequentlyMentionedChips' */ './reviews/FrequentlyMentioned'));

const { productId } = useProduct();

const { isConsolidatedThirdVariantFlag } = useProductFlags();

const {
  reviews,
  page,
  starFiltering,
  sortBy,
  frequentlyMentioned,
  selectedFrequentlyMentioned,
  totalResults,
  searchQuery,
  fetchReviews,
  relatedProducts,
  resetFilters,
  reviewsHeader,
  isFiltered,
  isCondensable,
  isCondensed,
  toggleIsCondensed,
} = useReviews();
const { isReady: isStatisticsReady, totalCount } = useReviewsStatistics();
const noReviews = computed(() => Number(totalResults.value) === 0);

/**
 * @type {import('vue').Ref<Nullable<HTMLElement>>}
 */
const firstReviewRef = ref(null);
/**
 * @type {import('vue').Ref<Nullable<HTMLElement>>}
 */
const reviewsToggle = ref(null);
/**
 * @type {import('vue').Ref<Nullable<HTMLElement>>}
 */
const reviewSectionContainer = ref(null);

const handleShadow = () => {
  if (reviewsToggle.value && firstReviewRef.value && reviewSectionContainer.value) {
    const shadowHeight = reviewsToggle.value.getBoundingClientRect().top - firstReviewRef.value.getBoundingClientRect().top;
    if (!Number.isNaN(shadowHeight) && shadowHeight > 0) {
      reviewSectionContainer.value.style.setProperty('--height', `${shadowHeight}px`);
    }
  }
};
const {
  isMobile, isTablet, isDesktopMedium, isDesktop,
} = useDeviceType();
const reviewsIsVisible = ref(false);
const isInitialized = ref(false);
const isInitializing = ref(false);

watch([isMobile, isTablet, isDesktopMedium, isDesktop, reviewsIsVisible, isInitialized], handleShadow);

async function fetchAllReviews() {
  return Promise.allSettled([
    fetchReviews(),
    fetchReviews.photos(),
    fetchReviews.frequentlyMentioned(),
  ]);
}

const initialize = async () => {
  if (isInitialized.value || (isStatisticsReady.value && !totalCount.value)) return;
  isInitializing.value = true;
  await fetchAllReviews();
  isInitialized.value = true;
};

const { stop } = useIntersectionObserver(reviewSectionContainer, ([{ isIntersecting }]) => {
  if (!isIntersecting) {
    return;
  }
  reviewsIsVisible.value = isIntersecting;

  if (isInitialized.value && isInitializing.value) {
    stop();
    return;
  }

  initialize();
}, {
  rootMargin: '200px',
});

onMounted(() => {
  if (!noReviews.value) {
    const stats = relatedProducts.value[productId].reviewStatistics;
    // BV track for stats - these run once per page load
    trackPageView(stats, productId);
    trackImpression(reviews.value);
  }
});
/** @type {import('vue').Ref<number?>} */
const openCommentId = ref(null);

watch([
  page,
  starFiltering,
  sortBy,
  selectedFrequentlyMentioned,
  searchQuery,
], () => {
  fetchReviews();
});

const selectedMemberId = useSelectedMemberId(productId);

const showSkeleton = ref(false);

if (isConsolidatedThirdVariantFlag) {
  watch(selectedMemberId, async () => {
    showSkeleton.value = true;
    // filters need to be reset before fetching all reviews
    resetFilters();
    await fetchAllReviews();
    showSkeleton.value = false;
  }, { immediate: true });
}

const toggleBtnText = computed(() => `${isCondensed.value ? 'Read' : 'Hide' } Reviews`);

/**
 * @param {Number} reviewId
 */

const handleToggleComment = (reviewId) => {
  if (openCommentId.value === reviewId) {
    openCommentId.value = null;
  } else {
    openCommentId.value = reviewId;
  }
};

const { open } = useModal();
const INDIVIDUAL_REVIEW_MODAL = 'individual-review-modal';
/** @type import("vue").Ref<Review[]> */
const selectedReview = ref([]);
const clickedPhotoIndex = ref(0);
const photos = computed(() => selectedReview.value?.[0]?.photos?.map((photo) => ({ url: photo.sizes.normal.url, caption: '', reviewIndex: 0 })) || []);

const displayFullReview = (review, selectedPhotoIndex) => {
  selectedReview.value = [review];
  clickedPhotoIndex.value = selectedPhotoIndex;
  open(INDIVIDUAL_REVIEW_MODAL);
};
</script>

<style lang="scss" scoped>
.review-section-container {
  position: static;
  overflow: hidden;
  display: flex;
  flex-flow: column;

  &.condensed {
    position: relative;
    height: rem-calc(964);
    @include breakpoint(medium up) {
      height: rem-calc(760);
    }
    border-bottom: 1px solid $gray-3-color;

    .review-section {
      max-height: rem-calc(864);
      @include breakpoint(medium up) {
        max-height: rem-calc(660);
      }
      overflow: hidden;
    }

    .reviews-toggle {
      position: absolute;
      transform: translateX(-50%);
      left: 50%;
      bottom: rem-calc(32);
    }
  }
}

.review-section {
  display: flex;
  flex-flow: column
}

.reviews-toggle {
  width: 100%;
  max-width: rem-calc(335);
  align-self: center;
}

.review-section-container.condensed:after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  height: var(--height, rem-calc(105));
  bottom: rem-calc(82);
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.50) 28.13%, #FFF 88.02%);
}
</style>
